import { isNil, round } from "lodash";
import React, { useRef, useMemo, useId, useEffect, useState } from "react";
import { cn } from "@/shared/utils/shadcn";
import { areaColorsMap, ScoreChartProps } from ".";
import { Score } from "@/shared/services/api/graphql/generated";
import { PersonScore } from "@/features/people/hooks/useDimensionsWithScores";

interface ScoreChartGradientProps extends ScoreChartProps {
  score?: Score;
  showAverageScoreNumber?: boolean;
}

const HEIGT_ADJUSTMENT = 20; // Adjust for the average score text

const ScoreChartGradient: React.FC<ScoreChartGradientProps> = (props) => {
  const {
    scores,
    renderScore,
    showArea = false,
    areaColor,
    averageScore,
    width: defaultWidth = 125,
    showAverageScoreNumber,
    height = 24,
    score,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const uniqueId = useId(); // Generate unique ID for this component instance
  const [actualWidth, setActualWidth] = useState(defaultWidth);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setActualWidth(containerRef.current.offsetWidth || defaultWidth);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [defaultWidth]);

  const { minScore, maxScore } = useMemo(() => {
    const values = scores.map((score) => score.value || 0);
    return {
      minScore: Math.min(...values),
      maxScore: Math.max(...values),
    };
  }, [scores]);

  const scoreValue = score?.value ?? 0;
  const left = `${scoreValue}%`;

  // Create unique gradient ID for this component instance
  const gradientId = `horizontalGradient-${uniqueId}`;

  const hasScore = !isNil(score);

  return (
    <div ref={containerRef} className={cn("w-full relative")}>
      <svg
        width="100%"
        height={height + HEIGT_ADJUSTMENT} // Add extra space for the average score text
        viewBox={`0 0 ${actualWidth} ${height + HEIGT_ADJUSTMENT}`}
        preserveAspectRatio="none"
        className="absolute -top-[20px] left-0"
      >
        {/* Define gradient */}
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FFCEA3" />
            <stop offset="50%" stopColor="#F1F1F1" />
            <stop offset="100%" stopColor="#30BE9F" />
          </linearGradient>
        </defs>

        {/* Horizontal gradient rectangle */}
        <rect
          x={0}
          y={height / 2 + HEIGT_ADJUSTMENT - 2} // Center the rect and adjust for height
          width={actualWidth}
          height={4}
          fill={`url(#${gradientId})`}
          rx={2} // Rounded corners
        />

        {/* Area and end lines */}
        {showArea && areaColor && (
          <rect
            x={(minScore / 100) * actualWidth}
            y={HEIGT_ADJUSTMENT + 2} // Start below the average score text
            width={((maxScore - minScore) / 100) * actualWidth}
            height={height - 4}
            fill={areaColorsMap[areaColor].bg}
            opacity={0.7}
          />
        )}

        {/* Average score text */}
        {!isNil(averageScore) && (
          <>
            <line
              x1={(averageScore / 100) * actualWidth}
              y1={HEIGT_ADJUSTMENT} // Start below the average score text
              x2={(averageScore / 100) * actualWidth}
              y2={height + HEIGT_ADJUSTMENT} // Extend to the bottom of the chart
              stroke={areaColor ? areaColorsMap[areaColor].stroke : "#000000"}
              strokeWidth="2"
            />
            {showAverageScoreNumber && (
              <text
                x={(averageScore / 100) * actualWidth}
                y={15} // Position above the chart
                textAnchor="middle"
                fill={areaColor ? areaColorsMap[areaColor].stroke : "#000000"}
                fontSize="12"
                fontWeight="600"
              >
                {round(averageScore)}
              </text>
            )}
          </>
        )}
      </svg>

      {hasScore && (
        <div className="relative" style={{ height, width: actualWidth }}>
          <div
            key={`${score.obj_type}-${score.obj_id}`}
            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ left }}
          >
            {renderScore(score as PersonScore)}
          </div>
        </div>
      )}

      {/* Labels */}
      <div className="flex justify-between text-xs font-medium text-gray-500 px-1 mt-2">
        <span style={{ color: "#FFB575" }}>LOW</span>
        <span style={{ color: "#91939A" }}>NEUTRAL</span>
        <span style={{ color: "#30BE9F" }}>HIGH</span>
      </div>
    </div>
  );
};

export default ScoreChartGradient;
